import { Button, ContentLayout, Header, Input, Modal, SpaceBetween, Table } from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { FC, useEffect, useMemo, useState } from 'react';
import { createItem, deleteItem, fetchItems, fetchItemsByIds } from '../api/generic';
import { adjustInventoryQuantity } from '../api/inventory';
import PeriodFilterControls, { ViewMode } from '../components/PeriodFilterControls';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import InventoryItem from '../models/Inventory';
import { getEndOfQuarter, getEndOfYear, getStartOfQuarter, getStartOfYear } from '../utils/date';
import { formatNumber } from '../utils/number';
import { prefixCollection } from '../utils/prefix';
import { toDate } from '../utils/timestamp';

interface InventoryCheckEntry {
  itemId: string;
  expectedQuantity: number;
  actualQuantity: number;
  discrepancy: number;
  unit: string;
  note?: string;
}

interface InventoryCheckRecord {
  id?: string;
  checkedBy: string;
  date: Timestamp;
  items: InventoryCheckEntry[];
}

interface User {
  id: string;
  name?: string;
  email?: string;
}

const InventoryCheck: FC = () => {
  const alert = useAlertContext();
  const { tenant, userObject } = useAuthContext();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [physicalCounts, setPhysicalCounts] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [pastChecks, setPastChecks] = useState<InventoryCheckRecord[]>([]);
  const [userMap, setUserMap] = useState<Record<string, User>>({});
  const [viewMode, setViewMode] = useState<ViewMode>('quarter');
  const [activeDate, setActiveDate] = useState<Date>(new Date());

  const [applyCheck, setApplyCheck] = useState<InventoryCheckRecord | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const loadInventory = async () => {
      const items = await fetchItems<InventoryItem>(prefixCollection('inventory', tenant));
      setInventory(items);
    };
    loadInventory();
  }, [tenant]);

  const loadChecks = async () => {
    const allChecks = await fetchItems<InventoryCheckRecord>(prefixCollection('inventoryChecks', tenant));

    const getPeriodBounds = (): [Date, Date] => {
      if (viewMode === 'quarter') return [getStartOfQuarter(activeDate), getEndOfQuarter(activeDate)];
      return [getStartOfYear(activeDate), getEndOfYear(activeDate)];
    };
    const [start, end] = getPeriodBounds();

    const filtered = allChecks.filter((check) => {
      const date = toDate(check.date);
      return date >= start && date <= end;
    });
    setPastChecks(filtered);

    const userIds = Array.from(new Set(filtered.map((c) => c.checkedBy).filter(Boolean)));
    const users = await fetchItemsByIds<User>('users', userIds);
    const userLookup: Record<string, User> = {};
    for (const u of users) userLookup[u.id] = u;
    setUserMap(userLookup);
  };

  useEffect(() => {
    loadChecks();
  }, [tenant, activeDate, viewMode]);

  const handleSaveCheck = async () => {
    const items: InventoryCheckEntry[] = inventory.map((item) => {
      const expected = item.quantity;
      const actual = Number(physicalCounts[item.id] ?? expected);
      const discrepancy = actual - expected;
      return {
        itemId: item.id,
        expectedQuantity: expected,
        actualQuantity: actual,
        discrepancy,
        unit: item.unit,
        ...(notes[item.id] !== undefined ? { note: notes[item.id] } : {}),
      };
    });

    const record: InventoryCheckRecord = {
      checkedBy: userObject?.id ?? 'unknown',
      date: Timestamp.now(),
      items,
    };

    await createItem(prefixCollection('inventoryChecks', tenant), record);
    await loadChecks();

    setPhysicalCounts({});
    setNotes({});

    alert.success('Inventory check saved. Apply the check to update the inventory.');
  };

  const handlePeriodChange = (dir: 'prev' | 'next') => {
    setActiveDate((prev) => {
      const month = prev.getMonth();
      const year = prev.getFullYear();
      if (viewMode === 'quarter') {
        return new Date(year, dir === 'prev' ? month - 3 : month + 3, 1);
      } else {
        return new Date(dir === 'prev' ? year - 1 : year + 1, 0, 1);
      }
    });
  };

  const invById = useMemo(
    () => Object.fromEntries(inventory.map((i) => [i.id, { name: i.name, unit: i.unit }])),
    [inventory],
  );

  const opFor = (expected: number, actual: number) =>
    actual > expected ? 'add' : actual < expected ? 'remove' : 'update';

  return (
    <ContentLayout header={<Header variant="h1">Quarterly Inventory Check</Header>}>
      <Table
        columnDefinitions={[
          { header: 'Item', cell: (i) => i.name, isRowHeader: true },
          { header: 'System Qty', cell: (i) => `${i.quantity} ${i.unit}` },
          {
            header: 'Physical Count',
            cell: (i) => (
              <Input
                type="number"
                value={String(physicalCounts[i.id] ?? i.quantity)}
                onChange={({ detail }) =>
                  setPhysicalCounts((prev) => ({
                    ...prev,
                    [i.id]: Number(detail.value),
                  }))
                }
              />
            ),
          },
          {
            header: 'Discrepancy',
            cell: (i) => {
              const actual = physicalCounts[i.id] ?? i.quantity;
              const diff = actual - i.quantity;
              const formatted = formatNumber(diff, 2);
              return diff === 0 ? '✓' : `${diff > 0 ? '+' : ''}${formatted}`;
            },
          },
          {
            header: 'Note',
            cell: (i) => (
              <Input
                value={notes[i.id] ?? ''}
                onChange={({ detail }) => setNotes((prev) => ({ ...prev, [i.id]: detail.value }))}
              />
            ),
          },
        ]}
        items={inventory}
        stickyHeader
        variant="embedded"
      />

      <SpaceBetween direction="vertical" size="m">
        <Button onClick={handleSaveCheck} variant="primary">
          Save Inventory Check
        </Button>

        <Header variant="h2">Past Inventory Checks</Header>
        <PeriodFilterControls
          viewMode={viewMode}
          setViewMode={setViewMode}
          activeDate={activeDate}
          handlePeriodChange={handlePeriodChange}
        />

        <Table
          columnDefinitions={[
            {
              header: 'Date',
              cell: (c) => toDate(c.date).toLocaleDateString(),
            },
            {
              header: 'Checked By',
              cell: (c) => userMap[c.checkedBy]?.name ?? userMap[c.checkedBy]?.email ?? c.checkedBy,
            },
            { header: 'Items', cell: (c) => c.items.length },
            {
              header: 'Discrepancies',
              cell: (c) => {
                const count = c.items.filter((i) => i.discrepancy !== 0).length;
                return count === 0 ? 'None' : count;
              },
            },
            {
              header: 'Actions',
              cell: (c) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="link"
                    onClick={async () => {
                      if (confirm('Are you sure you want to delete this inventory check?')) {
                        // Call your generic delete helper
                        await deleteItem(prefixCollection('inventoryChecks', tenant), c?.id ?? 'Not found');
                        // Refresh the list after delete
                        setPastChecks((prev) => prev.filter((check) => check.id !== c.id));
                      }
                    }}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="link"
                    onClick={() => {
                      setApplyCheck(c);
                      setViewOnly(false);
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="link"
                    onClick={() => {
                      setApplyCheck(c);
                      setViewOnly(true);
                    }}
                  >
                    View Details
                  </Button>
                </SpaceBetween>
              ),
            },
          ]}
          items={[...pastChecks].sort((a, b) => toDate(b.date).getTime() - toDate(a.date).getTime())}
          stickyHeader
          variant="borderless"
          trackBy="id"
        />
      </SpaceBetween>

      <Modal
        onDismiss={() => setApplyCheck(null)}
        visible={!!applyCheck}
        header={viewOnly ? 'Inventory Check Details' : 'Apply Inventory Check'}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setApplyCheck(null)}>
              Close
            </Button>
            {!viewOnly && (
              <Button
                variant="primary"
                loading={applying}
                onClick={async () => {
                  if (!applyCheck) return;
                  setApplying(true);

                  for (const entry of applyCheck.items) {
                    if (entry.discrepancy !== 0) {
                      await adjustInventoryQuantity(
                        entry.itemId,
                        {
                          operation: 'update',
                          quantity: entry.actualQuantity,
                          action: 'inventory_check',
                          userId: userObject?.id,
                        },
                        tenant,
                      );
                    }
                  }

                  setInventory((prev) =>
                    prev.map((item) => {
                      const applied = applyCheck.items.find((i) => i.itemId === item.id);
                      return applied ? { ...item, quantity: applied.actualQuantity } : item;
                    }),
                  );

                  setApplying(false);
                  setApplyCheck(null);
                  alert.success('Inventory adjusted successfully.');
                }}
              >
                Confirm & Apply
              </Button>
            )}
          </SpaceBetween>
        }
      >
        {applyCheck && (
          <SpaceBetween direction="vertical" size="s">
            <p>{viewOnly ? 'The following changes were found:' : 'Review the following changes:'}</p>
            {applyCheck.items.every((i) => i.discrepancy === 0) && <p>No discrepancies found.</p>}
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {applyCheck.items
                .filter((i) => i.discrepancy !== 0)
                .map((i) => {
                  const meta = invById[i.itemId];
                  const name = meta?.name ?? i.itemId; // fallback
                  const unit = meta?.unit ?? i.unit;
                  const op = opFor(i.expectedQuantity, i.actualQuantity);
                  return (
                    <li key={i.itemId}>
                      <strong>{name}</strong>: {i.expectedQuantity} → {i.actualQuantity} {unit}{' '}
                      <em>
                        ({op}
                        {op !== 'update' ? ` ${formatNumber(Math.abs(i.discrepancy))} ${unit}` : ''})
                      </em>
                      {i.note ? ` — ${i.note}` : ''}
                    </li>
                  );
                })}
            </ul>
          </SpaceBetween>
        )}
      </Modal>
    </ContentLayout>
  );
};

export default InventoryCheck;
